import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  ButtonBase,
  TextField,
  Link,
  Switch
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";

import { useAuthStore } from "@/store/auth-store";

// ---- Types ----------------------------------------------------------------

/** A broker account produced by this panel. */
export interface ConnectedBrokerAccount {
  id: string;
  /** User-chosen nickname. */
  name: string;
  provider: string;
  paper: boolean;
}

interface ConnectBrokerPanelProps {
  /** "grid" starts on the broker picker; "paper" jumps straight to the
   *  WhiteHat Paper nickname step. */
  initialView?: "grid" | "paper";
  /** Called when an account is created (paper or a supported broker). */
  onCreated: (account: ConnectedBrokerAccount) => void;
  /** Back/cancel out of the panel. */
  onCancel: () => void;
}

interface Broker {
  id: string;
  name: string;
  /** Fallback tile color when no logo asset is present. */
  color: string;
  /** Logo file under `public/brokers/`. Omit to use the color fallback. */
  logo?: string;
  /** The WHB simulated paper broker — the default, always available. */
  paper?: boolean;
  /** A broker WHB currently integrates (real connect flow). */
  supported?: boolean;
}

/**
 * Brokers. WHB currently integrates **Alpaca, Webull and Public** and
 * offers the **WhiteHatBots Paper** account (the default). Every other
 * broker is listed so users can see it and request support — tapping
 * one opens the broker-request form.
 *
 * Logos are loaded from `public/brokers/<id>.svg` (drop the real brand
 * assets there). Until a file exists, the tile falls back to a colored
 * initial so the grid never breaks.
 */
const BROKERS: Broker[] = [
  {
    id: "alpaca",
    name: "Alpaca",
    color: "#e8b50b",
    logo: "alpaca.png",
    supported: true
  },
  {
    id: "whb-paper",
    name: "WhiteHatBots Paper",
    color: "#26282d",
    logo: "whb-paper.jpg",
    paper: true
  },
  {
    id: "webull",
    name: "Webull",
    color: "#1f6feb",
    logo: "webull.svg",
    supported: true
  },
  {
    id: "public",
    name: "Public",
    color: "#7c3aed",
    logo: "public.jpeg",
    supported: true
  },
  {
    id: "tradestation",
    name: "TradeStation",
    color: "#2563eb",
    logo: "tradestation.jpeg"
  },
  {
    id: "ibkr",
    name: "Interactive Brokers",
    color: "#d4232f",
    logo: "ibkr.svg"
  },
  { id: "etrade", name: "E*Trade", color: "#6d28d9", logo: "etrade.svg" },
  {
    id: "robinhood",
    name: "Robinhood",
    color: "#16a34a",
    logo: "robinhood.png"
  },
  {
    id: "robomarkets",
    name: "Robomarkets (DE)",
    color: "#0ea5e9",
    logo: "robomarkets.svg"
  },
  { id: "tradier", name: "Tradier", color: "#ea580c", logo: "tradier.svg" },
  { id: "forex", name: "FOREX.com", color: "#dc2626", logo: "forex.png" },
  { id: "tda", name: "TD Ameritrade", color: "#15803d", logo: "tda.svg" },
  {
    id: "icmarkets",
    name: "IC Markets",
    color: "#475569",
    logo: "icmarkets.jpeg"
  },
  { id: "cmc", name: "CMC Markets", color: "#0891b2", logo: "cmc.jpeg" },
  { id: "saxo", name: "Saxo Bank / Saxo", color: "#1e3a8a", logo: "saxo.svg" },
  { id: "oanda", name: "OANDA", color: "#b91c1c", logo: "oanda.svg" }
];

const WHB_PAPER = BROKERS.find((b) => b.paper)!;

/** Per-broker connection details for the supported brokers. */
interface BrokerConnectInfo {
  /** Real broker page opened (new tab) when the user taps Connect. */
  connectUrl: string;
  /** Step-by-step sign-up / connect instructions (Learn More). */
  steps: { title: string; body: string }[];
}

/**
 * Connection guidance for each supported broker. Each broker has its
 * own sign-up / login / link flow; "Connect" opens the broker's real
 * site in a new tab where the user signs in and approves access.
 */
const BROKER_CONNECT: Record<string, BrokerConnectInfo> = {
  alpaca: {
    connectUrl: "https://alpaca.markets/",
    steps: [
      {
        title: "Choose Alpaca",
        body: "Tap Connect — you'll be taken to Alpaca's secure sign-in page."
      },
      {
        title: "New to Alpaca?",
        body: "Sign up with your email and a password, complete the short application (name, date of birth, address), and verify your email."
      },
      {
        title: "Already have an account?",
        body: "Just log in with your Alpaca email and password."
      },
      {
        title: "Approve access",
        body: "Alpaca asks whether to allow WhiteHatBots to connect — tap Allow."
      },
      {
        title: "Pick Paper or Live",
        body: "Choose whether to link your Alpaca paper or live trading account."
      },
      {
        title: "Done",
        body: "You'll return to WhiteHatBots with Alpaca connected."
      }
    ]
  },
  webull: {
    connectUrl: "https://www.webull.com/",
    steps: [
      {
        title: "Choose Webull",
        body: "Tap Connect — you'll be taken to Webull's secure sign-in page."
      },
      {
        title: "New to Webull?",
        body: "Sign up with your email or phone number, set a password, and complete identity verification."
      },
      {
        title: "Already have an account?",
        body: "Log in with your Webull credentials."
      },
      {
        title: "Approve access",
        body: "Allow WhiteHatBots to connect to your Webull account."
      },
      {
        title: "Done",
        body: "You'll return to WhiteHatBots with Webull connected."
      }
    ]
  },
  public: {
    connectUrl: "https://public.com/",
    steps: [
      {
        title: "Choose Public",
        body: "Tap Connect — you'll be taken to Public's secure sign-in page."
      },
      {
        title: "New to Public?",
        body: "Sign up with your email, set a password, and complete the brokerage application."
      },
      {
        title: "Already have an account?",
        body: "Log in with your Public credentials."
      },
      {
        title: "Approve access",
        body: "Allow WhiteHatBots to connect to your Public account."
      },
      {
        title: "Done",
        body: "You'll return to WhiteHatBots with Public connected."
      }
    ]
  }
};

// ---- Component ------------------------------------------------------------

/**
 * Connect Broker Account panel (Sergey 2026-05-21). Three views:
 *
 *   grid        — broker picker. Supported brokers + WHB Paper open the
 *                 nickname step; unsupported brokers open the request
 *                 form.
 *   nickname    — name + create the account (paper or supported broker).
 *   request     — "Request Broker Account Support" form for brokers WHB
 *                 doesn't integrate yet; on submit shows a confirmation.
 *
 * Embedded both in the standalone Add-Account page and the Marketplace
 * Buy flow, so it's a plain panel, not its own drawer/page.
 */
export function ConnectBrokerPanel({
  initialView = "grid",
  onCreated,
  onCancel
}: ConnectBrokerPanelProps) {
  const user = useAuthStore((s) => s.user);

  const [view, setView] = useState<
    "grid" | "nickname" | "brokerConnect" | "instructions" | "request" | "sent"
  >(initialView === "paper" ? "nickname" : "grid");
  // Which broker the nickname / connect step is for. Defaults to WHB
  // Paper — the only broker reachable when initialView="paper".
  const [chosen, setChosen] = useState<Broker>(WHB_PAPER);
  const [nickname, setNickname] = useState("");
  // Paper / Live toggle on the broker-connect screen.
  const [connectMode, setConnectMode] = useState<"paper" | "live">("live");

  // Broker-request form fields.
  const [reqBroker, setReqBroker] = useState("");
  const [reqWebsite, setReqWebsite] = useState("");
  const [reqName, setReqName] = useState(user?.name ?? "");
  const [reqEmail, setReqEmail] = useState(user?.email ?? "");
  const [reqNotify, setReqNotify] = useState(false);

  function pickBroker(b: Broker) {
    if (b.paper) {
      setChosen(b);
      setNickname("");
      setView("nickname");
    } else if (b.supported) {
      // Supported brokers (Alpaca / Webull / Public) get their own
      // connect screen — Paper/Live toggle + real sign-in flow.
      setChosen(b);
      setConnectMode("live");
      setView("brokerConnect");
    } else {
      setReqBroker(b.name);
      setReqWebsite("");
      setView("request");
    }
  }

  function handleCreate() {
    onCreated({
      id: `acct-${Date.now().toString(36)}`,
      name: nickname.trim() || "WhiteHat Paper Account",
      provider: chosen.name,
      paper: !!chosen.paper
    });
  }

  function handleConnect() {
    // Open the broker's real sign-in / sign-up page so the user can
    // authenticate and approve access there; the in-app account record
    // is created optimistically so the Buy flow can continue.
    const info = BROKER_CONNECT[chosen.id];
    if (info) {
      window.open(info.connectUrl, "_blank", "noopener,noreferrer");
    }
    onCreated({
      id: `acct-${Date.now().toString(36)}`,
      name: `${chosen.name} ${connectMode === "paper" ? "Paper" : "Live"}`,
      provider: chosen.name,
      paper: connectMode === "paper"
    });
  }

  // ---- Nickname step ----
  if (view === "nickname") {
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Connect Broker Account
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
          <BrokerLogo broker={chosen} size={36} />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{chosen.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {chosen.paper ? "Simulated paper account" : "Live broker account"}
            </Typography>
          </Box>
        </Stack>

        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "text.secondary", mb: 1 }}
        >
          Set your nickname for this broker account:
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={chosen.paper ? "My paper account" : `My ${chosen.name}`}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          autoFocus
        />

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
            onClick={() =>
              initialView === "paper" ? onCancel() : setView("grid")
            }
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            sx={{ flex: 1, fontWeight: 700, borderRadius: 2 }}
          >
            Create
          </Button>
        </Stack>
      </Box>
    );
  }

  // ---- Broker connect step (Alpaca / Webull / Public) ----
  if (view === "brokerConnect") {
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Connect Broker Account
        </Typography>

        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <BrokerLogo broker={chosen} size={44} />
          <Typography sx={{ fontWeight: 700 }}>{chosen.name}</Typography>
          <Link
            component="button"
            type="button"
            onClick={() => setView("instructions")}
            sx={{ fontWeight: 700 }}
          >
            Learn More ›
          </Link>
        </Stack>

        {/* Paper / Live segmented toggle */}
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
          {(["paper", "live"] as const).map((m) => (
            <Button
              key={m}
              fullWidth
              variant={connectMode === m ? "contained" : "outlined"}
              color="primary"
              onClick={() => setConnectMode(m)}
              sx={{ fontWeight: 700, borderRadius: 2 }}
            >
              {m === "paper" ? "Paper" : "Live"}
            </Button>
          ))}
        </Stack>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleConnect}
          sx={{ py: 1.25, fontWeight: 700, borderRadius: 5 }}
        >
          Connect
        </Button>

        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Link
            component="button"
            type="button"
            onClick={() => setView("instructions")}
            sx={{ fontWeight: 600 }}
          >
            Other ways to Connect
          </Link>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          Connecting opens {chosen.name}'s secure sign-in page in a new tab.
          Sign in and approve WhiteHatBots access to link your account.
        </Typography>

        <Button
          variant="text"
          color="inherit"
          startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
          onClick={() => setView("grid")}
          sx={{ mt: 1, fontWeight: 700 }}
        >
          Back
        </Button>
      </Box>
    );
  }

  // ---- Connection instructions (Learn More) ----
  if (view === "instructions") {
    const steps = BROKER_CONNECT[chosen.id]?.steps ?? [];
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          How to Sign Up and Connect {chosen.name} to WhiteHatBots
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          Follow these steps to sign up for {chosen.name} and connect it to
          WhiteHatBots.
        </Typography>
        <Box
          component="ol"
          sx={{
            m: 0,
            pl: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25
          }}
        >
          {steps.map((s) => (
            <Box component="li" key={s.title}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {s.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {s.body}
              </Typography>
            </Box>
          ))}
        </Box>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setView("brokerConnect")}
            sx={{ fontWeight: 700, borderRadius: 5, px: 3 }}
          >
            Close
          </Button>
        </Stack>
      </Box>
    );
  }

  // ---- Request-sent confirmation ----
  if (view === "sent") {
    return (
      <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
        <CheckCircleRounded sx={{ fontSize: 52, color: "success.main" }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
          Request received
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mx: "auto", maxWidth: 360 }}
        >
          Thanks — we've logged your request for <strong>{reqBroker}</strong>{" "}
          and we'll do our best to add it.
          {reqNotify
            ? " We'll email you when it's supported."
            : ""}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setView("grid")}
          sx={{ mt: 3, fontWeight: 700, borderRadius: 2 }}
        >
          Back to brokers
        </Button>
      </Box>
    );
  }

  // ---- Request form ----
  if (view === "request") {
    const canSubmit =
      reqBroker.trim().length > 0 &&
      reqName.trim().length > 0 &&
      reqEmail.trim().length > 0;
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Request Broker Account Support
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Fill out the form below to request support for your broker, and
          we'll do our best to add it in the near future.
        </Typography>

        <ReqLabel required>
          I need this broker to be supported (Broker Name)
        </ReqLabel>
        <TextField
          fullWidth
          size="small"
          value={reqBroker}
          onChange={(e) => setReqBroker(e.target.value)}
        />

        <ReqLabel>Broker Website</ReqLabel>
        <TextField
          fullWidth
          size="small"
          placeholder="https://"
          value={reqWebsite}
          onChange={(e) => setReqWebsite(e.target.value)}
        />

        <ReqLabel required>Your Name</ReqLabel>
        <TextField
          fullWidth
          size="small"
          value={reqName}
          onChange={(e) => setReqName(e.target.value)}
        />

        <ReqLabel required>E-mail</ReqLabel>
        <TextField
          fullWidth
          size="small"
          value={reqEmail}
          onChange={(e) => setReqEmail(e.target.value)}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 1.5 }}
        >
          <Switch
            checked={reqNotify}
            onChange={(e) => setReqNotify(e.target.checked)}
          />
          <Typography variant="body2">
            Inform me via e-mail when this broker is supported
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="center"
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setView("grid")}
            sx={{ fontWeight: 700, borderRadius: 5, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={() => setView("sent")}
            sx={{ fontWeight: 700, borderRadius: 5, px: 3 }}
          >
            Submit
          </Button>
        </Stack>
      </Box>
    );
  }

  // ---- Broker grid ----
  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Connect Broker Account
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Why do you need a broker account?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A broker account is needed for trading. For testing, choose the
        WhiteHatBots Paper account.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Can't find your broker?{" "}
        <Link
          component="button"
          type="button"
          onClick={() => {
            setReqBroker("");
            setReqWebsite("");
            setView("request");
          }}
          sx={{ fontWeight: 600 }}
        >
          Request your broker connection
        </Link>
      </Typography>

      {/* Broker grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1
        }}
      >
        {BROKERS.map((b) => (
          <ButtonBase
            key={b.id}
            onClick={() => pickBroker(b)}
            sx={(t: Theme) => ({
              position: "relative",
              flexDirection: "column",
              gap: 0.75,
              py: 1.5,
              px: 0.5,
              borderRadius: 2,
              border: `1px solid ${t.palette.divider}`,
              "&:hover": { bgcolor: "action.hover" }
            })}
          >
            <BrokerLogo broker={b} size={40} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}
            >
              {b.name}
            </Typography>
            {(b.supported || b.paper) && (
              <Box
                sx={(t) => ({
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: t.palette.success.main
                })}
              />
            )}
          </ButtonBase>
        ))}
      </Box>

      {/* Paper-account disclaimer */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={(t) => ({
          mt: 1.5,
          p: 1.25,
          borderRadius: 1.5,
          bgcolor: alpha(t.palette.info.main, 0.1)
        })}
      >
        <InfoOutlined sx={{ fontSize: 18, color: "info.main", mt: 0.1 }} />
        <Typography variant="caption" color="text.secondary">
          WHB integrates Alpaca, Webull and Public (green dot). A
          WhiteHatBots Paper account is a simulated trading account that
          mirrors real market conditions — test and run bots safely,
          without risking real money. Tap any other broker to request it.
        </Typography>
      </Stack>

      <Button
        variant="text"
        color="inherit"
        startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
        onClick={onCancel}
        sx={{ mt: 1.5, fontWeight: 700 }}
      >
        Back
      </Button>
    </Box>
  );
}

// ---- Internals ------------------------------------------------------------

/**
 * Broker tile logo. The brand asset (from `public/brokers/`) fills the
 * square edge-to-edge — square app-icon logos (Alpaca, Robinhood, …)
 * carry their own background colour right out to the tile edge with no
 * white frame; transparent/wide logos sit on the dark card. Falls back
 * to a colored initial — or the bot glyph for the WHB Paper account —
 * when no asset exists or the image fails to load.
 */
function BrokerLogo({ broker, size }: { broker: Broker; size: number }) {
  const [failed, setFailed] = useState(false);

  if (broker.logo && !failed) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          overflow: "hidden",
          flexShrink: 0,
          display: "flex"
        }}
      >
        <Box
          component="img"
          src={`/brokers/${broker.logo}`}
          alt={broker.name}
          onError={() => setFailed(true)}
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
    );
  }

  // Fallback tile — bot glyph for WHB Paper, colored initial otherwise.
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: broker.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}
    >
      {broker.paper ? (
        <SmartToyOutlined sx={{ fontSize: size * 0.55, color: "#fff" }} />
      ) : (
        <Typography
          sx={{ fontWeight: 800, color: "#fff", fontSize: size * 0.4 }}
        >
          {broker.name[0]}
        </Typography>
      )}
    </Box>
  );
}

/** Field label for the broker-request form. */
function ReqLabel({
  children,
  required
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mt: 1.75,
        mb: 0.5,
        fontWeight: 700,
        color: "text.secondary"
      }}
    >
      {children}
      {required && <Box component="span" sx={{ color: "error.main" }}> *</Box>}
    </Typography>
  );
}
